import {
  forwardRef,
  useEffect,
  useId,
  useImperativeHandle,
  useRef,
  useState,
  type ChangeEvent,
  type ClipboardEvent,
  type FocusEvent,
  type KeyboardEvent,
  type MouseEvent,
} from 'react';
import inputStyles from '@/shared/ui/Input/Input.module.css';
import { Clock3 } from 'lucide-react';
import type { InputProps } from '@/shared/ui/Input/Input';
import styles from './TimeInput.module.css';

export type TimeInputProps = Omit<InputProps, 'type' | 'value' | 'onChange'> & {
  value: string;
  onChange: (value: string) => void;
};

function formatarHorario(value: string) {
  const digitos = value.replace(/\D/g, '').slice(0, 4);
  if (digitos.length <= 2) return digitos;
  return `${digitos.slice(0, 2)}:${digitos.slice(2)}`;
}

function partesHorario(value: string) {
  const [hora = '', minuto = ''] = value.split(':');
  return { hora: hora.padEnd(2, '0').slice(0, 2), minuto: minuto.padEnd(2, '0').slice(0, 2) };
}

const horas = Array.from({ length: 24 }, (_, index) => String(index).padStart(2, '0'));
const minutos = Array.from({ length: 60 }, (_, index) => String(index).padStart(2, '0'));

export const TimeInput = forwardRef<HTMLInputElement, TimeInputProps>(function TimeInput(
  {
    value,
    onChange,
    onBlur,
    onFocus,
    onClick,
    onKeyDown,
    onPaste,
    className,
    label,
    error,
    helperText,
    required,
    id,
    ...rest
  },
  ref,
) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const errorId = error ? `${inputId}-error` : undefined;
  const inputRef = useRef<HTMLInputElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [segmento, setSegmento] = useState<'hora' | 'minuto'>('hora');
  const [digitosDoSegmento, setDigitosDoSegmento] = useState('');

  useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

  useEffect(() => {
    if (!open) return undefined;
    const fecharAoClicarFora = (event: globalThis.MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', fecharAoClicarFora);
    return () => document.removeEventListener('mousedown', fecharAoClicarFora);
  }, [open]);

  const selecionarSegmento = (proximo: 'hora' | 'minuto', limparBuffer = true) => {
    setSegmento(proximo);
    if (limparBuffer) setDigitosDoSegmento('');
    requestAnimationFrame(() => {
      const input = inputRef.current;
      if (!input) return;
      const inicio = proximo === 'hora' ? 0 : 3;
      input.focus();
      input.setSelectionRange(inicio, inicio + 2);
    });
  };

  const atualizarHorario = (proximoSegmento: 'hora' | 'minuto', valor: string) => {
    const atual = partesHorario(value);
    const proximo = proximoSegmento === 'hora'
      ? `${valor}:${atual.minuto}`
      : `${atual.hora}:${valor}`;
    onChange(proximo);
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChange(formatarHorario(event.target.value));
    setDigitosDoSegmento('');
  };

  const handleFocus = (event: FocusEvent<HTMLInputElement>) => {
    onFocus?.(event);
    selecionarSegmento('hora');
  };

  const handleClick = (event: MouseEvent<HTMLInputElement>) => {
    onClick?.(event);
    const posicao = event.currentTarget.selectionStart ?? 0;
    selecionarSegmento(posicao <= 2 ? 'hora' : 'minuto');
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    onKeyDown?.(event);
    if (event.defaultPrevented) return;
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault();
      selecionarSegmento(event.key === 'ArrowLeft' ? 'hora' : 'minuto');
      return;
    }
    if (event.key === 'Escape') {
      setOpen(false);
      return;
    }
    if (event.key === 'Backspace' || event.key === 'Delete') {
      event.preventDefault();
      setDigitosDoSegmento('');
      if (!value) {
        selecionarSegmento('hora');
        return;
      }
      const atual = partesHorario(value);
      if (atual.hora === '00' && atual.minuto === '00') {
        onChange('');
        selecionarSegmento('hora');
        return;
      }
      if (segmento === 'minuto') {
        if (atual.minuto !== '00') {
          atualizarHorario('minuto', '00');
          selecionarSegmento('minuto');
        } else {
          atualizarHorario('hora', '00');
          selecionarSegmento('hora');
        }
        return;
      }
      if (atual.hora !== '00') {
        atualizarHorario('hora', '00');
        selecionarSegmento('hora');
      } else {
        atualizarHorario('minuto', '00');
        selecionarSegmento('minuto');
      }
      return;
    }
    if (!/^\d$/.test(event.key) || event.ctrlKey || event.metaKey || event.altKey) return;

    event.preventDefault();
    const input = inputRef.current;
    const selecaoCompleta = input != null
      && input.selectionStart === 0
      && input.selectionEnd === input.value.length;
    const segmentoAtual = selecaoCompleta ? 'hora' : segmento;
    const bufferAtual = selecaoCompleta ? '' : digitosDoSegmento;
    const novoBuffer = `${bufferAtual}${event.key}`.slice(-2);
    const limite = segmentoAtual === 'hora' ? 23 : 59;
    const numero = Number(novoBuffer);
    if (novoBuffer.length === 2 && numero <= limite) {
      atualizarHorario(segmentoAtual, novoBuffer);
      setDigitosDoSegmento('');
      if (segmentoAtual === 'hora') selecionarSegmento('minuto');
      return;
    }
    setDigitosDoSegmento(event.key);
    atualizarHorario(segmentoAtual, `0${event.key}`);
    selecionarSegmento(segmentoAtual, false);
  };

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    onPaste?.(event);
    if (event.defaultPrevented) return;
    event.preventDefault();
    onChange(formatarHorario(event.clipboardData.getData('text')));
  };

  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    onBlur?.(event);
    window.setTimeout(() => {
      if (!rootRef.current?.contains(document.activeElement)) setOpen(false);
    }, 0);
  };

  const alterarParte = (parte: 'hora' | 'minuto', proximoValor: string) => {
    atualizarHorario(parte, proximoValor);
    setSegmento(parte);
    setDigitosDoSegmento('');
  };

  return (
    <div className={`${inputStyles.field} ${className ?? ''}`.trim()} ref={rootRef}>
      {label ? (
        <label htmlFor={inputId} className={inputStyles.label}>
          {label}
          {required ? (
            <>
              {' '}
              <span className={inputStyles.required} aria-hidden="true">*</span>
            </>
          ) : null}
        </label>
      ) : null}
      <div className={styles.inputWrap}>
        <input
          {...rest}
          ref={inputRef}
          id={inputId}
          className={`${inputStyles.input} ${styles.input} ${error ? inputStyles.inputError : ''}`}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          placeholder="HH:mm"
          maxLength={5}
          value={value}
          aria-invalid={Boolean(error)}
          aria-describedby={errorId}
          aria-required={required || undefined}
          onChange={handleChange}
          onFocus={handleFocus}
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onBlur={handleBlur}
        />
        <button
          type="button"
          className={styles.trigger}
          aria-label="Selecionar horário"
          aria-expanded={open}
          aria-haspopup="dialog"
          disabled={rest.disabled}
          tabIndex={-1}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => setOpen((atual) => !atual)}
        >
          <Clock3 size={20} aria-hidden />
        </button>
        {open ? (
          <div className={styles.popover} role="dialog" aria-label="Selecionar horário">
            <label>
              Hora
              <select
                value={partesHorario(value).hora}
                onChange={(event) => alterarParte('hora', event.target.value)}
              >
                {horas.map((hora) => <option key={hora} value={hora}>{hora}</option>)}
              </select>
            </label>
            <span className={styles.separator} aria-hidden>:</span>
            <label>
              Minuto
              <select
                value={partesHorario(value).minuto}
                onChange={(event) => alterarParte('minuto', event.target.value)}
              >
                {minutos.map((minuto) => <option key={minuto} value={minuto}>{minuto}</option>)}
              </select>
            </label>
          </div>
        ) : null}
      </div>
      {error ? (
        <span id={errorId} className={inputStyles.error} role="alert">
          {error}
        </span>
      ) : helperText ? (
        <span className={inputStyles.helper}>{helperText}</span>
      ) : null}
    </div>
  );
});
